// ---- data ----
// The words carry no diacritics: the game spells bare glyphs, and DIA holds the
// vowelled form — what the TTS reads, and the حركة each letter wears in the tray
// and the sky (facesOf, core/letters.ts), so a letter has a mark on it where its
// row here puts one and nowhere else. h is how tall each animal stands, in Jiddo's
// units — he is 2.4. speed is in units a second. Shortest word first, which is
// the order rounds are dealt in.
//
// e is drawn nowhere in the scene. It is here because the recording portal's
// inventory is scraped out of the games rather than hand-written, and with the
// SVG game retired this is the only place that knows the donkey is 🫏 — which
// build-items.py needs both for his word card and for his welcome (ترحيب-حمار).
//
// The roster is deliberately short. Every name on it is a clip a real person has
// to sit down and say, so it grows one animal at a time. Adding one means a row
// here, a row in DIA, its letters in NAMES, a .glb beside index.html, a picture
// in img/, a brain in brains.ts, his two lines for it in GAMES in
// scripts/generate-audio.py (which reads the word and its letters from here)
// — then check-words.js and build-items.py.
// swims marks an animal the pond is open to — the duck, the penguin, the sea
// lion, the walrus and the fish — see core/water.ts.
// brain names the animal's entry in BRAINS (brains.ts); a name that is not
// there does not type-check. The tables here stay plain literals with no logic
// among them, because check-words.js and build-items.py read them out of this
// file as text — as they read them out of index.html when it was one file.
import type {BrainName} from './brains';

export interface AnimalSpec{
  w:string;          // the name, bare glyphs: what the round spells
  e:string;          // its emoji, for the recording portal's cards and nothing else
  h:number;          // how tall it stands, in Jiddo's units
  file:string;       // its model, beside index.html
  speed:number;      // units a second
  brain:BrainName;   // what it does in the field
  swims?:boolean;    // the pond is open to it
  water?:boolean;    // it lives there: it arrives on the pond and never leaves it
}
export const ANIMALS:AnimalSpec[]=[{w:'دب',e:'🐻‍❄️',h:1.7,file:'polarbear.glb',speed:0.6,brain:'bear'},
               {w:'قط',e:'🐱',h:1.0,file:'cat.glb',speed:0.7,brain:'cat'},
               {w:'فظ',e:'🦭',h:1.3,file:'walrus.glb',speed:0.4,brain:'walrus',swims:true},
               {w:'فيل',e:'🐘',h:2.9,file:'elephant.glb',speed:0.7,brain:'elephant'},
               {w:'بطة',e:'🦆',h:0.72,file:'duck.glb',speed:0.5,brain:'duck',swims:true},
               {w:'حية',e:'🐍',h:0.85,file:'snake.glb',speed:0.55,brain:'snake'},
               {w:'ديك',e:'🐓',h:0.9,file:'rooster.glb',speed:0.55,brain:'rooster'},
               {w:'فأر',e:'🐭',h:0.45,file:'mouse.glb',speed:1.6,brain:'mouse'},
               {w:'كلب',e:'🐕',h:1.1,file:'dog.glb',speed:0.9,brain:'dog'},
               {w:'صوص',e:'🐥',h:0.5,file:'chick.glb',speed:0.6,brain:'chick'},
               {w:'ذئب',e:'🐺',h:1.3,file:'wolf.glb',speed:0.85,brain:'wolf'},
               {w:'ثور',e:'🐂',h:1.9,file:'ox.glb',speed:0.55,brain:'ox'},
               {w:'رنة',e:'🦌',h:1.7,file:'reindeer.glb',speed:0.75,brain:'reindeer'},
               {w:'فهد',e:'🐆',h:1.15,file:'cheetah.glb',speed:1.7,brain:'cheetah'},
               {w:'ضبع',e:'🦴',h:1.2,file:'hyena.glb',speed:0.85,brain:'hyena'},
               {w:'نسر',e:'🦅',h:1.1,file:'eagle.glb',speed:0.4,brain:'eagle'},
               {w:'حمار',e:'🫏',h:1.92,file:'donkey.glb',speed:0.8,brain:'donkey'},
               {w:'أرنب',e:'🐰',h:0.72,file:'rabbit.glb',speed:0.9,brain:'rabbit'},
               {w:'بقرة',e:'🐄',h:1.9,file:'cow.glb',speed:0.6,brain:'cow'},
               {w:'بومة',e:'🦉',h:0.9,file:'owl.glb',speed:0.4,brain:'owl'},
               {w:'ثعلب',e:'🦊',h:1.05,file:'fox.glb',speed:0.75,brain:'fox'},
               {w:'خروف',e:'🐑',h:1.25,file:'sheep.glb',speed:0.55,brain:'sheep'},
               {w:'سمكة',e:'🐟',h:0.55,file:'goldfish.glb',speed:0.7,brain:'fish',swims:true,water:true},
               {w:'غزال',e:'🦌',h:1.55,file:'gazelle.glb',speed:0.9,brain:'gazelle'},
               {w:'غراب',e:'🐦‍⬛',h:0.85,file:'crow.glb',speed:0.45,brain:'crow'},
               {w:'ببغاء',e:'🦜',h:0.85,file:'parrot.glb',speed:0.45,brain:'parrot'},
               {w:'فقمة',e:'🦭',h:1.1,file:'sealion.glb',speed:0.5,brain:'sealion',swims:true},
               {w:'خنزير',e:'🐖',h:1.2,file:'pig.glb',speed:0.6,brain:'pig'},
               {w:'دجاجة',e:'🐔',h:0.85,file:'hen.glb',speed:0.55,brain:'hen'},
               {w:'جاموس',e:'🐃',h:1.95,file:'buffalo.glb',speed:0.55,brain:'buffalo'},
               {w:'يمامة',e:'🕊️',h:0.75,file:'dove.glb',speed:0.45,brain:'dove'},
               {w:'حمامة',e:'🐦',h:0.75,file:'pigeon.glb',speed:0.45,brain:'pigeon'},
               {w:'نعامة',e:'🪶',h:2.2,file:'ostrich.glb',speed:1.5,brain:'ostrich'},
               {w:'بطريق',e:'🐧',h:0.95,file:'penguin.glb',speed:0.45,brain:'penguin',swims:true},
               {w:'راكون',e:'🦝',h:0.9,file:'raccoon.glb',speed:0.7,brain:'raccoon'},
               {w:'سلحفاة',e:'🐢',h:0.55,file:'tortoise.glb',speed:0.22,brain:'tortoise'}];
export const NAMES:Record<string,string>={'ا':'أَلِف','أ':'أَلِف','ب':'بَاء','ة':'تَاء مَرْبُوطَة','ث':'ثَاء','ح':'حَاء','خ':'خَاء','د':'دَال','ر':'رَاء','س':'سِين','ط':'طَاء','ع':'عَيْن',
  'ف':'فَاء','ق':'قَاف','ك':'كَاف','ل':'لَام','م':'مِيم','ن':'نُون','و':'وَاو','ز':'زَيْن','ج':'جِيم','غ':'غَيْن','ص':'صَاد','ه':'هَاء','ض':'ضَاد','ظ':'ظَاء','ي':'يَاء','ء':'هَمْزَة','ذ':'ذَال','ئ':'هَمْزَة'};
export const DIA:Record<string,string>={'فيل':'فِيل','بطة':'بَطَّة','حمار':'حِمَار','أرنب':'أَرْنَب','بقرة':'بَقَرَة','حية':'حَيَّة','ديك':'دِيك','سلحفاة':'سُلَحْفَاة','بومة':'بُومَة','ثعلب':'ثَعْلَب','خروف':'خَرُوف','خنزير':'خِنْزِير','فأر':'فَأْر','قط':'قِطّ','كلب':'كَلْب','دجاجة':'دَجَاجَة','دب':'دُبّ','سمكة':'سَمَكَة','غزال':'غَزَال','صوص':'صُوص','ذئب':'ذِئْب','ثور':'ثَوْر','جاموس':'جَامُوس','رنة':'رَنَّة','فهد':'فَهْد','ضبع':'ضَبْع','نسر':'نَسْر','غراب':'غُرَاب','يمامة':'يَمَامَة','حمامة':'حَمَامَة','ببغاء':'بَبَّغَاء','نعامة':'نَعَامَة','بطريق':'بَطْرِيق','فقمة':'فُقْمَة','فظ':'فَظّ','راكون':'رَاكُون'};
// Nothing longer than MAXLEN letters. buildSlots() sizes the tray to whatever
// word is in it, so a longer one does not overflow — it just gets leaner
// letters, and past six they are too lean to read at arm's length on a phone.
// Six is also the roster's longest name, سلحفاة, so it is the ceiling and not
// a number to drift past. scripts/check-words.js asserts nothing outgrows it.
export const MAXLEN=6;
// Jiddo's lines are recorded, never written (see jiddo/audio/README.md). An
// arriving animal is welcomed by name when it has a take of its own — R_ترحيب-فيل
// — and these are the fallbacks for one that does not. Only the كتار lines may
// mention the animals already standing there, so they wait for a field that has
// some. ترحيب-3 has no take yet; pickClip skips a slot nobody has filled.
export const REACT:{upTo:number;clips:string[]}[]=[{upTo:5,clips:['ترحيب-1','ترحيب-2','ترحيب-3']},
             {upTo:1e9,clips:['ترحيب-كتار-1','ترحيب-كتار-2','ترحيب-كتار-3']}];
// His hello: said once, as the game opens, before the first round's letters
// rise (announceRound, round.ts). One take, since it is heard once a visit.
// Recorded only, like every line of his: with no take the game opens as it did.
export const INTRO='تعريف';
